import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FieldRenderer } from "@/components/admin/cms/CmsFields";
import HistoryDialog from "@/components/admin/cms/HistoryDialog";
import ScheduleControl from "@/components/admin/cms/ScheduleControl";
import { can, MANAGE_CONTENT } from "@/lib/format";
import { toast } from "sonner";
import { ExternalLink, Lock, History, Save, Eye, UploadCloud } from "lucide-react";

// Generic editor for a single page's intro/hero copy, driven by a PAGE_INTROS
// schema entry: { label, key, livePath, description, fields[], defaults{} }.
//
// Workflow mirrors the homepage: Save Draft (private), Preview (admin-only
// ?preview=1), and Save & Publish (writes the live key + syncs the draft).
const META = ["key", "updated_at", "updated_by", "updated_by_name", "_id"];
const hasContent = (d) => d && typeof d === "object" && Object.keys(d).some((k) => !META.includes(k));

export default function PageIntroEditor({ intro }) {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_CONTENT);
  const draftKey = `${intro.key}_draft`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const buildPayload = () => {
    const payload = {};
    intro.fields.forEach((f) => { payload[f.key] = data ? data[f.key] : intro.defaults[f.key]; });
    return payload;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Prefer an in-progress draft; otherwise the published content; else defaults.
      let source = null;
      try {
        const d = await api.get(`/admin/content/${intro.key}_draft`);
        if (hasContent(d.data)) source = d.data;
      } catch { /* no draft */ }
      if (!source) {
        try {
          const p = await api.get(`/admin/content/${intro.key}`);
          if (hasContent(p.data)) source = p.data;
        } catch { /* no published */ }
      }
      setData({ ...intro.defaults, ...(source || {}) });
    } catch {
      setData({ ...intro.defaults });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intro.key]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await api.put(`/admin/content/${draftKey}`, buildPayload());
      toast.success("Draft saved (not yet live).");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save draft");
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  const preview = async () => {
    const ok = await saveDraft();
    if (ok) window.open(`${intro.livePath}?preview=1`, "_blank", "noopener");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      await api.put(`/admin/content/${intro.key}`, payload);
      await api.put(`/admin/content/${draftKey}`, payload); // keep draft in sync with live
      toast.success(`${intro.label} page updated \u2014 changes are live.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div data-testid={`cms-intro-loading-${intro.key}`} className="text-[var(--muted)] py-8">Loading…</div>;
  }

  return (
    <div data-testid={`cms-intro-${intro.key}`}>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wide text-[var(--muted)]">{intro.description}</div>
        <div className="flex items-center gap-2">
          {editable && (
            <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)} data-testid={`cms-intro-history-${intro.key}`} className="text-[var(--text-2)] hover:text-[var(--text)]">
              <History size={13} className="mr-1" />History
            </Button>
          )}
          <a href={intro.livePath} target="_blank" rel="noreferrer" className="text-sm text-[#9a7b3f] hover:opacity-80 transition-opacity flex items-center gap-1" data-testid={`cms-intro-viewlive-${intro.key}`}>
            View live <ExternalLink size={13} />
          </a>
          {!editable && (
            <Badge variant="outline" className="bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]">
              <Lock size={11} className="mr-1" />View only
            </Badge>
          )}
        </div>
      </div>

      <Card className="border-[var(--border-lch)] p-5 space-y-4 max-w-2xl">
        {intro.fields.map((f) => (
          <FieldRenderer key={f.key} field={f} value={data[f.key]} onChange={(v) => set(f.key, v)} disabled={!editable} />
        ))}
      </Card>

      {editable && (
        <div className="mt-4 max-w-2xl">
          <ScheduleControl contentKey={intro.key} getPayload={buildPayload} />
        </div>
      )}

      {editable && (
        <div className="flex flex-wrap items-center justify-end gap-2 sticky bottom-4 mt-5 max-w-2xl">
          <Button onClick={saveDraft} disabled={savingDraft} variant="outline" data-testid={`cms-intro-save-draft-${intro.key}`} className="bg-[var(--panel)]">
            <Save size={14} className="mr-1.5" />{savingDraft ? "Saving…" : "Save Draft"}
          </Button>
          <Button onClick={preview} variant="outline" data-testid={`cms-intro-preview-${intro.key}`} className="bg-[var(--panel)]">
            <Eye size={14} className="mr-1.5" />Preview
          </Button>
          <Button onClick={save} disabled={saving} data-testid={`cms-intro-save-${intro.key}`} className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)] shadow-lg">
            <UploadCloud size={14} className="mr-1.5" />{saving ? "Saving…" : "Save & Publish"}
          </Button>
        </div>
      )}

      <HistoryDialog contentKey={intro.key} open={historyOpen} onOpenChange={setHistoryOpen} onRestored={load} />
    </div>
  );
}
